import React from 'react'
import { Image as ImageIcon, PlayCircle } from 'lucide-react'

/**
 * MediaSlot — the reusable, ALWAYS-VISIBLE media area (shared by the marketing
 * inner pages so the whole site is media-forward and consistent). Styled for the
 * dark cinematic theme (matches the redesigned homepage + landing.css tokens):
 *  • video URL → autoplay/mute/loop/playsInline <video> (poster if provided)
 *  • image URL → <img> (object-cover)
 *  • neither   → a premium DARK placeholder frame: soft light-on-dark gradient,
 *                faint brand warmth, hairline dashed border, centred icon +
 *                caption ("Hero media" / "Product video") so the slot is obvious
 *                to admins/agents — never a broken image, and readable on dark.
 *
 * The `onDark` prop is retained for API compatibility; every surface here is
 * already tuned for the dark theme.
 */
export default function MediaSlot({ image, video, poster, label = 'Media', sublabel, kind = 'image', ratioClass = 'aspect-[16/9]', onDark = false, bleed = false }) {
  // `bleed` = fill a positioned parent edge-to-edge (full-bleed banners);
  // otherwise a self-contained rounded, aspect-ratioed card. Additive/opt-in,
  // so existing callers keep their exact behaviour.
  const frame = bleed
    ? 'absolute inset-0 w-full h-full overflow-hidden'
    : `relative w-full ${ratioClass} rounded-3xl overflow-hidden border border-white/10`
  const shadow = bleed ? '' : 'shadow-[0_40px_80px_-44px_rgba(0,0,0,0.85)]'

  if (video) {
    return (
      <div className={`${frame} ${shadow} bg-black`}>
        <video src={video} poster={poster || undefined} className="absolute inset-0 w-full h-full object-cover" autoPlay muted loop playsInline />
      </div>
    )
  }
  if (image) {
    return (
      <div className={`${frame} ${shadow}`}>
        <img src={image} alt={label} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      </div>
    )
  }

  // Empty → premium DARK placeholder frame.
  const Icon = kind === 'video' ? PlayCircle : ImageIcon
  return (
    <div className={`${bleed ? 'absolute inset-0 w-full h-full overflow-hidden' : `relative w-full ${ratioClass} rounded-3xl overflow-hidden border border-white/10`} bg-[#0c0e13]`}>
      {/* soft base gradient (light-on-dark) */}
      <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.05), rgba(255,255,255,0.01))' }} />
      {/* faint brand warmth */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(90% 70% at 50% 0%, rgba(229,55,43,0.10), transparent 60%)' }} />
      {/* dashed inner frame reads as a media slot */}
      <div className="absolute inset-3 sm:inset-4 rounded-2xl border border-dashed border-white/15" />
      {/* centred label */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
        <div className="w-14 h-14 rounded-2xl border border-white/10 bg-white/5 shadow-sm flex items-center justify-center">
          <Icon size={24} className="text-white/35" />
        </div>
        <div>
          <p className="text-[13px] font-semibold text-[#c7cdd8]">{label}</p>
          <p className="text-[11px] mt-0.5 text-[#8b93a3]">{sublabel || (kind === 'video' ? 'Autoplay video · add in the CMS' : 'Add an image in the CMS')}</p>
        </div>
      </div>
    </div>
  )
}
